import { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { type Api, ApiError } from "../api";

interface Brand {
  businessName: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
}

/**
 * UX-5 SMB essentials — logo, favicon and brand color in Settings.
 * Every site already has a monogram favicon by default; uploads override
 * it. The logo also becomes the default for header blocks in the editor.
 */
export function BrandCard({ api }: { api: Api }) {
  const [brand, setBrand] = useState<Brand | null>(null);
  const [noSite, setNoSite] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBrand(await api.getBrand());
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setNoSite(true);
      } else {
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Could not load brand settings.",
        );
      }
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const uploadAndSet = async (
    field: "logoUrl" | "faviconUrl",
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || busy) return;
    setBusy(field);
    setError(null);
    try {
      const { item } = await api.uploadMedia(
        file,
        field === "logoUrl" ? "Logo" : "Favicon",
      );
      setBrand(await api.setBrand({ [field]: item.url }));
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Upload failed — try a PNG or WebP under 8MB.",
      );
    } finally {
      setBusy(null);
    }
  };

  const clear = async (field: "logoUrl" | "faviconUrl") => {
    if (busy) return;
    setBusy(field);
    try {
      setBrand(await api.setBrand({ [field]: null }));
    } finally {
      setBusy(null);
    }
  };

  const setColor = async (value: string) => {
    setBrand((b) => (b ? { ...b, primaryColor: value } : b));
    try {
      setBrand(await api.setBrand({ primaryColor: value }));
    } catch {
      // keep local value; next load reconciles
    }
  };

  if (noSite) return null;

  return (
    <section className="card brand-card">
      <div className="card-head">
        <div>
          <h2>Brand</h2>
          <p className="muted">
            Your logo and icon, everywhere they belong — the browser tab, the
            site header, link previews.
          </p>
        </div>
      </div>
      {error && <p className="notice">{error}</p>}
      {brand && (
        <div className="brand-rows">
          <div className="brand-row">
            <div className="brand-row-copy">
              <h3>Logo</h3>
              <p className="muted small">
                Shows in your site header — pick it in the visual editor's
                header section, or upload here to make it available.
              </p>
            </div>
            <div className="brand-row-controls">
              {brand.logoUrl && (
                <img className="brand-preview" src={brand.logoUrl} alt="Logo" />
              )}
              <label className="btn-secondary brand-upload">
                {busy === "logoUrl" ? "Uploading…" : "Upload logo"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/avif"
                  onChange={(e) => void uploadAndSet("logoUrl", e)}
                  hidden
                />
              </label>
              {brand.logoUrl && (
                <button type="button" onClick={() => void clear("logoUrl")}>
                  Remove
                </button>
              )}
            </div>
          </div>

          <div className="brand-row">
            <div className="brand-row-copy">
              <h3>Browser icon</h3>
              <p className="muted small">
                Without an upload, visitors see a monogram of your initial in
                your brand color.
              </p>
            </div>
            <div className="brand-row-controls">
              <img
                className="brand-preview brand-favicon"
                src={brand.faviconUrl ?? "/favicon-preview.svg"}
                alt="Site icon"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
              <label className="btn-secondary brand-upload">
                {busy === "faviconUrl" ? "Uploading…" : "Upload icon"}
                <input
                  type="file"
                  accept="image/png,image/svg+xml,image/webp"
                  onChange={(e) => void uploadAndSet("faviconUrl", e)}
                  hidden
                />
              </label>
              {brand.faviconUrl && (
                <button type="button" onClick={() => void clear("faviconUrl")}>
                  Use monogram
                </button>
              )}
            </div>
          </div>

          <div className="brand-row">
            <div className="brand-row-copy">
              <h3>Brand color</h3>
              <p className="muted small">Used by the monogram icon.</p>
            </div>
            <div className="brand-row-controls">
              <input
                type="color"
                value={brand.primaryColor ?? "#2c5c45"}
                onChange={(e) => void setColor(e.target.value)}
                aria-label="Brand color"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
